// src/Components/ContactButton/ContactButton.js
import React, { useContext, useState, useEffect } from "react";
import client from "../../api/client";
import { AuthContext } from "../../contextStore/AuthContext";
import { useNavigate } from "react-router-dom";

// Helper to load Razorpay script
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

export default function ContactButton({
  machineId,
  onContact,
  onRequirePlans,
  redirectToOnLogin,
  children,
}) {
  const { token, user } = useContext(AuthContext) || {};
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState(null);
  const [canView, setCanView] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [plansForModal, setPlansForModal] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [buyerPlans, setBuyerPlans] = useState([]);

  // Fetch buyer plans on mount
  useEffect(() => {
    const fetchBuyerPlans = async () => {
      try {
        const res = await client.get('/api/plans?type=buyer');
        if (res.data?.plans) {
          setBuyerPlans(res.data.plans);
        }
      } catch (err) {
        console.error("Failed to fetch buyer plans", err);
      }
    };
    fetchBuyerPlans();
  }, []);

  // Helper to notify UI refresh
  function safeDispatchRefresh() {
    try {
      window.dispatchEvent(new Event("buyerProfileRefresh"));
    } catch (e) {
      try {
        const ev = document.createEvent("Event");
        ev.initEvent("buyerProfileRefresh", true, true);
        window.dispatchEvent(ev);
      } catch (_) { }
    }
  }

  // Try contact endpoints
  const tryEndpoints = async () => {
    const endpoints = [
      { method: "get", url: `/api/machines/${machineId}/contact` },
      { method: "get", url: `/api/listings/${machineId}/contact` },
    ];

    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    for (let i = 0; i < endpoints.length; i++) {
      const { method, url } = endpoints[i];
      try {
        let res;
        if (method === "get") {
          res = await client.get(url, { headers });
        } else {
          res = await client.post(url, {}, { headers });
        }
        return { res, url };
      } catch (err) {
        if (!err || !err.response) {
          return { err, url };
        }
        const r = err.response;
        if (r.status === 404 && i < endpoints.length - 1) continue;
        return { err, url };
      }
    }
    return { err: new Error("No endpoints tried") };
  };

  // Main handler to view contact
  const handleViewContact = async () => {
    if (!user && !token) {
      const redirect = redirectToOnLogin || `/machines/${machineId}`;
      navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
      return;
    }

    setLoading(true);

    try {
      const result = await tryEndpoints();

      if (result && result.res) {
        const r = result.res;
        const data = r.data || {};

        if (data.ok && data.contact) {
          const c = {
            phone: data.contact.phone || data.contact.phone_number || null,
            email: data.contact.email || null,
            seller_name: data.contact.seller_name || null,
          };
          setContact(c);
          setCanView(true);
          if (onContact) onContact(c);
          safeDispatchRefresh();
          return;
        }

        if (data.ok && (data.phone || data.email)) {
          const c = {
            phone: data.phone || null,
            email: data.email || null,
            seller_name: data.seller_name || null
          };
          setContact(c);
          setCanView(true);
          if (onContact) onContact(c);
          safeDispatchRefresh();
          return;
        }

        if (data.ok && !(data.contact && data.contact.phone) && !data.phone) {
          alert("Seller has no phone number available.");
          return;
        }

        // Payment required
        if (r.status === 402 || data?.error === "payment_required") {
          if (onRequirePlans) {
            onRequirePlans(buyerPlans);
          } else {
            setPlansForModal(buyerPlans);
            setShowPlanModal(true);
          }
          return;
        }

        if (r.status === 401 || r.status === 403) {
          navigate(`/login?redirect=${encodeURIComponent(`/machines/${machineId}`)}`);
          return;
        }

        alert(data.message || "Unable to get contact details.");
        return;
      }

      if (result && result.err) {
        const err = result.err;
        const r = err.response;

        if (!r) {
          alert(err.message || "Network error.");
          return;
        }

        if (r.status === 402) {
          if (onRequirePlans) {
            onRequirePlans(buyerPlans);
          } else {
            setPlansForModal(buyerPlans);
            setShowPlanModal(true);
          }
          return;
        }

        if (r.status === 401 || r.status === 403) {
          navigate(`/login?redirect=${encodeURIComponent(`/machines/${machineId}`)}`);
          return;
        }

        alert(r.data?.message || r.data?.error || err.message || "Could not fetch contact.");
        return;
      }

      alert("Unable to fetch contact — unknown error.");
    } catch (ex) {
      console.error("[ContactButton] unexpected exception", ex);
      alert("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  // Find plan by ID from buyerPlans
  const findPlanById = (planId) => {
    return buyerPlans.find(p => p.id === planId);
  };

  // Purchase flow
  const startPurchaseForPlan = async (planId) => {
    if (!planId) return;
    setModalLoading(true);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // 🔥 FIND PLAN FROM ALREADY FETCHED PLANS
      const plan = findPlanById(planId);
      if (!plan) {
        throw new Error("Plan not found");
      }

      const amount = plan.price_cents ? (plan.price_cents / 100) : 1.00;
      const expires_days = plan.validity_days || 7;

      // 🔥 CORRECT create-order body with buyer planType
      const createOrderBody = {
        type: "plan",
        plan_id: planId,
        listing_id: machineId || null,
        amount: amount,
        currency: "INR",
        meta: {
          planType: "buyer", // 🔥 THIS MUST BE 'buyer'
          planId,
          listing_id: machineId || null,
          validity_days: expires_days
        },
      };

      console.log("SENDING create-order with:", createOrderBody.meta);

      const createRes = await client.post("/api/payments/create-order", createOrderBody, { headers });

      if (!(createRes && createRes.data && createRes.data.ok && createRes.data.payment)) {
        throw new Error("Create order failed");
      }

      const paymentId = createRes.data.payment.id;
      const isDev = process.env.NODE_ENV !== "production";

      if (isDev) {
        // Mock payment for development
        const mockBody = {
          payment_id: paymentId,
          provider_payment_id: `mock_${Date.now()}`,
          type: "plan",
          user_id: user?.id,
          plan_id: planId,
          listing_id: machineId || null,
          amount: amount,
          currency: "INR",
          expires_days: expires_days,
          meta: {
            planType: "buyer", // 🔥 THIS MUST BE 'buyer'
            planId,
            listing_id: machineId || null,
            validity_days: expires_days
          },
        };

        const mockRes = await client.post("/api/payments/mock/complete", mockBody, { headers });

        if (!(mockRes && mockRes.data && mockRes.data.ok)) {
          throw new Error("Mock payment failed");
        }

        // Wait for processing
        await new Promise(r => setTimeout(r, 2000));

        // Try to fetch contact
        try {
          const contactRes = await client.get(`/api/machines/${machineId}/contact`, { headers });

          if (contactRes.data && contactRes.data.ok) {
            const c = contactRes.data.contact
              ? {
                phone: contactRes.data.contact.phone || null,
                email: contactRes.data.contact.email || null,
                seller_name: contactRes.data.contact.seller_name || null
              }
              : {
                phone: contactRes.data.phone || null,
                email: contactRes.data.email || null,
                seller_name: contactRes.data.seller_name || null
              };

            setContact(c);
            setCanView(true);
            if (onContact) onContact(c);
            setShowPlanModal(false);
            safeDispatchRefresh();
            return;
          }
        } catch (contactErr) {
          console.error("Failed to fetch contact after payment", contactErr);
        }

        alert("Payment successful! Please refresh the page to see contact details.");
      } else {
        // Production Razorpay flow
        const razorpayOrder = createRes.data.razorpay_order;
        const keyId = createRes.data.razorpay_key_id || process.env.REACT_APP_RAZORPAY_KEY_ID;

        await loadScript("https://checkout.razorpay.com/v1/checkout.js");

        const options = {
          key: keyId,
          amount: razorpayOrder.amount,
          currency: razorpayOrder.currency,
          name: "Old Machine Market",
          description: "Buyer Plan for Contact Access",
          order_id: razorpayOrder.id,
          prefill: { name: user?.name || "", email: user?.email || "" },
          notes: {
            internal_payment_id: paymentId,
            plan_id: planId,
            listing_id: machineId,
            planType: "buyer"
          },
          handler: async function (response) {
            try {
              const verifyRes = await client.post("/api/payments/razorpay/verify", {
                razorpay_order_id: razorpayOrder.id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                type: "plan",
                user_id: user?.id,
                plan_id: planId,
                listing_id: machineId || null,
                amount: amount,
                currency: "INR",
                expires_days: expires_days,
                meta: {
                  planType: "buyer",
                  planId,
                  listing_id: machineId || null,
                  validity_days: expires_days
                }
              }, { headers });

              if (verifyRes.data.ok) {
                // Success - fetch contact
                const contactRes = await client.get(`/api/machines/${machineId}/contact`, { headers });
                if (contactRes.data.ok) {
                  const c = contactRes.data.contact
                    ? {
                      phone: contactRes.data.contact.phone,
                      email: contactRes.data.contact.email,
                      seller_name: contactRes.data.contact.seller_name
                    }
                    : {
                      phone: contactRes.data.phone,
                      email: contactRes.data.email,
                      seller_name: contactRes.data.seller_name
                    };

                  setContact(c);
                  setCanView(true);
                  if (onContact) onContact(c);
                  setShowPlanModal(false);
                  safeDispatchRefresh();
                }
              }
            } catch (err) {
              console.error("Payment verification failed", err);
            }
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      }

    } catch (err) {
      console.error("startPurchaseForPlan error", err);
      alert(err.message || "Failed to process payment");
    } finally {
      setModalLoading(false);
    }
  };

  // If already have contact, show it
  if (canView && contact) {
    return (
      <>
        <div className="contactBox">
          <div><strong>Phone:</strong> {contact.phone || "N/A"}</div>
          <div><strong>Email:</strong> {contact.email || "N/A"}</div>
          {contact.seller_name && <div><strong>Seller:</strong> {contact.seller_name}</div>}
        </div>
        {showPlanModal && (
          <PlanModal
            plans={plansForModal}
            onClose={() => setShowPlanModal(false)}
            onBuy={startPurchaseForPlan}
            loading={modalLoading}
          />
        )}
      </>
    );
  }

  return (
    <>
      <button onClick={handleViewContact} disabled={loading} className="btn contact-btn">
        {loading ? "Checking..." : children || "Contact Seller"}
      </button>

      {showPlanModal && (
        <PlanModal
          plans={plansForModal}
          onClose={() => setShowPlanModal(false)}
          onBuy={startPurchaseForPlan}
          loading={modalLoading}
        />
      )}
    </>
  );
}

// PlanModal component
function PlanModal({ plans = [], onClose, onBuy, loading }) {
  return (
    <div style={modalWrapperStyle}>
      <div style={modalStyle}>
        <button onClick={onClose} style={closeBtnStyle}>×</button>
        <h3>Choose a Buyer Plan to View Contact</h3>
        <p>Buyer plans let you view seller contact details</p>
        <div style={{ maxHeight: 400, overflowY: "auto" }}>
          {plans && plans.length ? (
            plans.map((p) => (
              <div key={p.id} style={planRowStyle}>
                <div>
                  <div style={{ fontWeight: 600 }}>{p.name}</div>
                  <div style={{ fontSize: 13 }}>
                    {p.contact_quota} contact(s) • {p.validity_days} days • ₹{(p.price_cents / 100).toFixed(2)}
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    {p.description?.split('\n')[0]}
                  </div>
                </div>
                <div style={{ alignSelf: "center" }}>
                  <button
                    onClick={() => onBuy(p.id)}
                    disabled={loading}
                    className="btn btn-primary"
                  >
                    {loading ? "Processing..." : `Buy ₹${(p.price_cents / 100).toFixed(2)}`}
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div>No buyer plans available</div>
          )}
        </div>
      </div>
    </div>
  );
}

// Styles
const modalWrapperStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
};

const modalStyle = {
  background: "#fff",
  padding: 20,
  borderRadius: 8,
  width: 600,
  maxWidth: "95%",
  position: "relative"
};

const closeBtnStyle = {
  position: "absolute",
  right: 12,
  top: 8,
  border: "none",
  background: "transparent",
  fontSize: 22,
  cursor: "pointer"
};

const planRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  padding: "12px 8px",
  borderBottom: "1px solid #eee"
};
